import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery, gql } from '@nerdwallet/apollo';
import classNames from 'classnames';
import dateformat from 'dateformat';
import { getLogger } from '@nerdwallet/logging';

import {
  Box,
  Link,
  Type,
  CenteredContainer,
  Divider,
} from '@nerdwallet/currency';
import Hero from '../../employee-branding/hero/hero';
import styles from './press-archive-browser.module.less';

import rootLogger from '~/client/lib/logger-utils';

const logger = getLogger('PressArchiveBrowser', rootLogger);

const articleQuery = gql`
  query pressReleases($offset: Int, $limit: Int) {
    pressReleases(
      pagination: { limit: $limit, offset: $offset }
      order: { field: DATE, sortDirection: DESC }
    ) {
      hasNextPage
      total
      currentOffset
      items {
        url
        title
        date
        mediaSites {
          name
        }
      }
    }
  }
`;

const OFFSET = 10;

const ArchiveNewsItem = ({ title, date, url, mediaSites = [] }) => {
  return (
    <Box
      marginBottom="4"
      paddingLeft="2"
      paddingRight="3"
      className={styles.pressBrowserItem}
    >
      <Box paddingBottom="2">
        <Type bold>
          <Link
            aria-label={
              mediaSites?.[0]?.name + title + dateformat(date, 'mmmm d, yyyy')
            }
            href={url}
          >
            {mediaSites?.[0]?.name}
          </Link>
          {mediaSites?.[0]?.name ? ', ' : ''} {dateformat(date, 'mmmm d, yyyy')}
        </Type>
      </Box>
      <Type>{title}</Type>
    </Box>
  );
};

const PressArchiveBrowser = () => {
  const { data, error, fetchMore } = useQuery(articleQuery, {
    variables: {
      offset: 0,
      limit: OFFSET,
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-and-network',
  });
  const current = data?.pressReleases?.currentOffset || 0;
  const [loading, setLoading] = useState(false);

  const handleUpdateQuery = (prev, { fetchMoreResult }) => {
    const element = document.getElementById('_archive-browser');
    element.focus();
    setLoading(false);
    if (!fetchMoreResult) return prev;
    return fetchMoreResult;
  };
  const handleSetNextSlide = () => {
    setLoading(true);
    fetchMore({
      variables: {
        offset: current + OFFSET,
        limit: OFFSET,
      },
      updateQuery: handleUpdateQuery,
    });
  };

  const handlePrevSlide = () => {
    setLoading(true);
    fetchMore({
      variables: {
        offset: current - OFFSET,
        limit: OFFSET,
      },
      updateQuery: handleUpdateQuery,
    });
  };
  const articles = data?.pressReleases?.items || [];
  const hasMore = data?.pressReleases?.hasNextPage;

  if (error) {
    logger.error(error);
    return null;
  }

  return (
    <React.Fragment>
      <Box marginBottom="2">
        <Hero
          backgroundColor="#FFE8E8"
          title="News Archive"
          copy="Catch up on a collection of news and stories that include commentary from NerdWallet."
          imageUrlMobile="https://www.nerdwallet.com/cdn/company/2021/hero/News-Hero-Mobile.jpg"
          imageUrlDesktop="https://www.nerdwallet.com/cdn/company/2021/hero/News-Hero-Desktop.jpg"
        />
      </Box>
      <CenteredContainer>
        <Box marginY={{ desktop: '5', mobile: '4' }}>
          <Box
            id="_archive-browser"
            tabIndex={0}
            aria-label="Press Archive Browser"
            className={styles.pressBrowserContainer}
            marginX={{ desktop: '4' }}
            paddingX={{ desktop: '5', mobile: '3' }}
          >
            <Box>
              <Box className={styles.pressBrowserCarousel}>
                <Box
                  className={classNames(styles.pressBrowserCarouselWrapper, {
                    [styles.pressBrowserCarouselLoading]: loading,
                  })}
                  justifyContent="center"
                  display="flex"
                  flexWrap="wrap"
                >
                  {articles.map((item) => (
                    <ArchiveNewsItem mediaSites={item.mediaSites} {...item} />
                  ))}
                </Box>
              </Box>
              <Divider />
              <Box display="flex" justifyContent="space-between" marginTop="4">
                {current > 0 ? (
                  <Link bold disabled={loading} onClick={handlePrevSlide}>
                    &larr; PREVIOUS
                  </Link>
                ) : (
                  <div />
                )}
                {hasMore ? (
                  <Link bold disabled={loading} onClick={handleSetNextSlide}>
                    MORE STORIES &rarr;
                  </Link>
                ) : (
                  <div />
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </CenteredContainer>
    </React.Fragment>
  );
};

ArchiveNewsItem.propTypes = {
  title: PropTypes.string,
  date: PropTypes.string,
  url: PropTypes.string,
  mediaSites: PropTypes.object,
};

export default PressArchiveBrowser;
